import { useMemo } from 'react';
import type { CivilAppellateWizardDraft } from '../../../types/civilAppellateCaseRequest';
import type { RequiredErrors } from '../../../utils/requiredValidation';
import WizardMultiSelectChips from '../../../components/ui/WizardMultiSelectChips';
import WizardSelect from '../../../components/wizard/WizardSelect';
import { useCaseRequestWizard } from '../../../hooks/useCaseRequestWizard';
import { FieldError } from './CivilAppellateWizardShell';
import { InstituteTypeSection } from '../../../components/wizard/InstituteTypeSection';
import type { InstituteTypeDraftFields } from '../../../utils/instituteTypeRules';
import {
  instituteFieldsForFileCategoryChange,
  instituteFieldsForSubCategoryChange,
} from '../../../utils/instituteTypeRules';
import { useCaseCategorization } from './useCaseCategorization';
import { useCivilAppellateLookups } from './useCivilAppellateLookups';
import { useWizardFieldLabels } from '../useWizardFieldLabels';

type Props = {
  draft: CivilAppellateWizardDraft;
  setDraft?: React.Dispatch<React.SetStateAction<CivilAppellateWizardDraft>>;
  editable?: boolean;
  errors?: RequiredErrors;
  onClearError?: (key: string) => void;
};

function SelectField({
  id,
  label,
  required,
  value,
  disabled,
  hasError,
  placeholder,
  options,
  onChange,
  onClear,
}: {
  id: string;
  label: string;
  required?: boolean;
  value: string;
  disabled?: boolean;
  hasError?: boolean;
  placeholder: string;
  options: { id: string; name: string }[];
  onChange: (id: string) => void;
  onClear?: () => void;
}) {
  return (
    <div className="ccr-fg">
      <label>
        {label} {required ? <span className="req">*</span> : null}
      </label>
      <WizardSelect
        id={id}
        value={value}
        disabled={disabled}
        hasError={hasError}
        placeholder={placeholder}
        options={options.map((o) => ({ id: o.id, label: o.name }))}
        onChange={onChange}
        onClear={onClear}
      />
    </div>
  );
}

export function CivilAppellateCaseCategorizationCard({
  draft,
  setDraft,
  editable = false,
  errors = {},
  onClearError,
}: Props) {
  const f = useWizardFieldLabels();
  const { switchFileCategory } = useCaseRequestWizard();
  const fileCategoriesEditable = editable;
  const cat = useCaseCategorization(draft.fileCategoryId, draft.subFileCategoryId, {
    enabled: editable || Boolean(draft.fileCategoryId),
  });
  const { corporateInstitutes } = useCivilAppellateLookups('step1');

  const fileLabel = cat.fileCategoryName || draft.fileCategory || '—';
  const subLabel = cat.subFileCategoryName || draft.subFileCategoryName || '—';

  const areaLabels =
    draft.applicableAreas.length > 0
      ? draft.applicableAreas
      : cat.resolveAreaLabels(draft.applicableAreaIds);

  const applicableAreaOptions = useMemo(() => {
    const byId = new Map(cat.applicableAreaOptions.map((o) => [o.id, o]));
    draft.applicableAreaIds.forEach((id, index) => {
      if (!id || byId.has(id)) return;
      const label = draft.applicableAreas[index]?.trim() || id;
      byId.set(id, { id, label });
    });
    return Array.from(byId.values());
  }, [cat.applicableAreaOptions, draft.applicableAreaIds, draft.applicableAreas]);

  const onFileCategoryChange = (id: string) => {
    if (!setDraft) return;
    const row = cat.fileCategories.find((c) => c.id === id);
    const name = row?.name ?? '';
    switchFileCategory({
      fromFlow: 'civil-appellate',
      categoryId: id,
      categoryName: name,
      categoryCode: row?.code,
      patch: instituteFieldsForFileCategoryChange(id, cat.fileCategories),
    });
    onClearError?.('fileCategoryId');
    onClearError?.('subFileCategoryId');
    onClearError?.('applicableAreas');
  };

  const onSubCategoryChange = (id: string) => {
    if (!setDraft || !draft.fileCategoryId) return;
    const name = cat.subCategories.find((s) => s.id === id)?.name ?? '';
    setDraft((d) => ({
      ...d,
      subFileCategoryId: id,
      subFileCategoryName: name,
      applicableAreaIds: [],
      applicableAreas: [],
      documents: [],
      ...instituteFieldsForSubCategoryChange(
        draft.fileCategoryId,
        id,
        cat.fileCategories,
        cat.subCategories,
      ),
    }));
    onClearError?.('subFileCategoryId');
    onClearError?.('applicableAreas');
    onClearError?.('instituteType');
    onClearError?.('corporateInstituteId');
    onClearError?.('corporateInstituteOther');
  };

  const onAreasChange = (ids: string[]) => {
    if (!setDraft) return;
    setDraft((d) => ({
      ...d,
      applicableAreaIds: ids,
      applicableAreas: cat.resolveAreaLabels(ids),
    }));
    onClearError?.('applicableAreas');
  };

  const fileCategorySelected =
    Boolean(draft.fileCategoryId) &&
    !cat.loadingCategories &&
    cat.fileCategories.some((c) => c.id === draft.fileCategoryId);

  const subCategorySelected =
    Boolean(draft.subFileCategoryId) &&
    !cat.loadingSubCategories &&
    cat.subCategories.some((s) => s.id === draft.subFileCategoryId);

  const subDisabled = !fileCategorySelected || cat.loadingSubCategories;
  const areasDisabled = !fileCategorySelected || !subCategorySelected || cat.loadingAreas;

  return (
    <div className="ccr-cat-card">
      <div className="ccr-fr c2" style={{ marginBottom: 14 }}>
        {fileCategoriesEditable && setDraft ? (
          <div>
            <SelectField
              id="capl-fileCategory"
              label={f.fileCategory}
              required
              value={draft.fileCategoryId}
              disabled={cat.loadingCategories}
              hasError={Boolean(errors.fileCategoryId)}
              placeholder={f.selectFileCategory}
              options={cat.fileCategories}
              onChange={onFileCategoryChange}
              onClear={() => onFileCategoryChange('')}
            />
            <FieldError message={errors.fileCategoryId} />
          </div>
        ) : (
          <div className="ccr-fg">
            <label>
              {f.fileCategory} <span className="req">*</span>
            </label>
            <input className="ccr-inp" value={fileLabel} readOnly disabled />
          </div>
        )}
        {fileCategoriesEditable && setDraft ? (
          <div>
            <SelectField
              id="capl-subFileCategory"
              label={f.subFileCategory}
              required
              value={draft.subFileCategoryId}
              disabled={subDisabled}
              hasError={Boolean(errors.subFileCategoryId)}
              placeholder={f.selectSubFileCategory}
              options={cat.subCategories}
              onChange={onSubCategoryChange}
              onClear={() => onSubCategoryChange('')}
            />
            <FieldError message={errors.subFileCategoryId} />
          </div>
        ) : (
          <div className="ccr-fg">
            <label>
              {f.subFileCategory} <span className="req">*</span>
            </label>
            <input className="ccr-inp" value={subLabel} readOnly disabled />
          </div>
        )}
      </div>

      <div className="ccr-fg" id="capl-applicableAreas" style={{ marginBottom: 14 }}>
        <label>{f.applicableAreas}</label>
        {editable && setDraft ? (
          <>
            <WizardMultiSelectChips
              id="capl-applicableAreas-input"
              options={applicableAreaOptions}
              value={draft.applicableAreaIds}
              onChange={onAreasChange}
              disabled={areasDisabled}
              hasError={Boolean(errors.applicableAreas)}
              placeholder={f.selectApplicableAreas}
              aria-label={f.applicableAreas}
            />
            <FieldError message={errors.applicableAreas} />
          </>
        ) : (
          <div className="ccr-area-tags read-only">
            {areaLabels.map((area) => (
              <span key={area} className="ccr-area-tag">
                {area}
              </span>
            ))}
            {areaLabels.length === 0 ? <span style={{ fontSize: 12, color: 'var(--t3)' }}>—</span> : null}
          </div>
        )}
      </div>

      <InstituteTypeSection
        idPrefix="capl"
        draft={draft}
        setDraft={
          setDraft
            ? (updater) => {
                setDraft((d) => {
                  const current: InstituteTypeDraftFields = {
                    instituteType: d.instituteType,
                    corporateInstituteId: d.corporateInstituteId,
                    corporateInstituteName: d.corporateInstituteName,
                    corporateInstituteOther: d.corporateInstituteOther,
                    corporateInstituteContactName: d.corporateInstituteContactName,
                    corporateInstitutePrefix: d.corporateInstitutePrefix,
                  };
                  const next = typeof updater === 'function' ? updater(current) : updater;
                  return { ...d, ...next };
                });
              }
            : undefined
        }
        editable={editable}
        errors={errors}
        onClearError={onClearError}
        fileCategoryId={draft.fileCategoryId}
        subFileCategoryId={draft.subFileCategoryId}
        fileCategories={cat.fileCategories}
        subCategories={cat.subCategories}
        corporateInstitutes={corporateInstitutes}
      />
    </div>
  );
}
